import React, { useMemo, useState } from 'react';
import {
    CloseOutlined,
    EditOutlined,
    EllipsisOutlined,
    FolderOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    PushpinOutlined,
    RightOutlined,
    SearchOutlined,
    UserAddOutlined,
    UserDeleteOutlined,
} from '@ant-design/icons';
import { Dropdown, message as antMessage } from 'antd';
import {
    PanelHeaderWrap,
    PanelHeaderRow,
    PanelHeaderLeft,
    PanelHeaderRight,
    PanelTitle,
} from '@layout/style';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Crown } from 'lucide-react';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { getAvatarColor } from '@utils/avatarColor';
import { resolveChatAvatarUrl, getParticipantAvatarPath } from '@utils/chatAvatar';
import ChatCircleAvatar from '@/components/ui/ChatCircleAvatar';

const ProjectTitleRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

/**
 * Chat header (Figma-like): left = chat context (project + title/subtitle), right = actions and participant (optional).
 * Note: Star icon intentionally omitted (per request).
 */
const HeaderSubtitle = styled.div`
    font-size: 12px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#6b7280')};
    line-height: 1.2;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeaderTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const MembersInlineWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    min-width: 0;
`;

const MembersCountButton = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: default;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: inherit;

    &:hover {
        opacity: 0.9;
    }
`;

const MemberAvatar = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 14px;
    border: 2px solid #fff;
    box-sizing: border-box;
    cursor: default;
`;

const MemberAvatarStack = styled(MemberAvatar)`
    width: 30px;
    height: 30px;
    font-size: 12px;
`;

/** Tilted “badge” crown on admin avatar (top-right overlap). */
const MemberAdminCrownBadge = styled.div`
    position: absolute;
    right: -5px;
    top: -6px;
    width: 17px;
    height: 17px;
    border-radius: 5px;
    background: linear-gradient(145deg, #fde68a 0%, #f59e0b 55%, #d97706 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fff;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.16);
    transform: rotate(38deg);
    z-index: 4;
    pointer-events: none;
`;

const MemberIconAvatarButton = styled.button`
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 2px solid #fff;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#374151')};

    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#f9fafb')};
    }
`;

const SearchInput = styled.input`
    width: 240px;
    height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#e5e7eb')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111827')};
    outline: none;
    box-sizing: border-box;
    &::placeholder {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#9ca3af')};
    }
    &:focus {
        border-color: ${({ theme }) => theme.colors.primary || 'var(--ant-color-primary)'};
        box-shadow: 0 0 0 3px ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(139,92,246,0.22)' : 'rgba(139,92,246,0.18)')};
    }
`;

const IconButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#e5e7eb')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#374151')};
    transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

    &:hover:not(:disabled) {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#f9fafb')};
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#3a3a3a' : '#d1d5db')};
    }
    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

export function ChatHeader({
    projectName,
    projectStatus,
    title,
    subtitle,
    initials,
    avatarImageUrl,
    avatarColor,
    selectionCount = 0,
    onInfo,
    onPin,
    isSearching = false,
    searchValue = '',
    onSearchChange,
    onSearchToggle,
    onSearchSubmit,
    isGroup = false,
    canManageGroup = false,
    onRenameGroup,
    onAddMember,
    onRemoveMember,
    /** Opens shared files / media / voice drawer (all chats). */
    onOpenChatFiles,
    /** Groups: opens the same right drawer with details + Media/Docs/Links. */
    onOpenGroupSidePanel,
    members = [],
    membersCount,
    adminId,
}) {
    const membersReducedMotion = useReducedMotion();
    const [membersExpanded, setMembersExpanded] = useState(false);

    const count = typeof membersCount === 'number' ? membersCount : Array.isArray(members) ? members.length : 0;

    const visibleMembers = useMemo(() => {
        const list = Array.isArray(members) ? members.slice(0, 12) : [];
        if (adminId == null || adminId === '') return list;
        const admin = String(adminId);
        return [...list].sort((a, b) => {
            const aid = String(a?.id ?? a?.userId ?? a?._id ?? '');
            const bid = String(b?.id ?? b?.userId ?? b?._id ?? '');
            const aIs = aid === admin;
            const bIs = bid === admin;
            if (aIs && !bIs) return -1;
            if (!aIs && bIs) return 1;
            return 0;
        });
    }, [members, adminId]);

    const stackPreview = useMemo(() => visibleMembers.slice(0, 3), [visibleMembers]);
    /** Horizontal overlap between stacked preview circles (px). */
    const stackOverlap = 10;
    /** Overlap for expanded member row (px). */
    const expandedMemberOverlap = 10;
    const stackClusterWidth =
        stackPreview.length <= 0 ? 0 : stackPreview.length === 1 ? 30 : 30 + (stackPreview.length - 1) * stackOverlap;

    const showSubtitle = !(isGroup && count > 0 && typeof subtitle === 'string' && /members/i.test(subtitle));

    const dur = membersReducedMotion ? 0.01 : 0.28;
    const stagger = membersReducedMotion ? 0 : 0.055;
    const memberSlideContainer = useMemo(
        () => ({
            hidden: {},
            show: {
                transition: { staggerChildren: stagger, delayChildren: membersReducedMotion ? 0 : 0.02 },
            },
            exit: {
                transition: { staggerChildren: stagger, staggerDirection: -1 },
            },
        }),
        [stagger, membersReducedMotion],
    );
    const memberSlideItem = useMemo(
        () => ({
            hidden: { opacity: 0, x: -20, scale: 0.92 },
            show: {
                opacity: 1,
                x: 0,
                scale: 1,
                transition: { duration: dur, ease: [0.22, 1, 0.36, 1] },
            },
            exit: { opacity: 0, x: -16, scale: 0.92, transition: { duration: Math.max(0.12, dur * 0.75) } },
        }),
        [dur],
    );

    const titleButtonStyle = {
        border: 0,
        background: 'transparent',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        textAlign: 'left',
        minWidth: 0,
        color: 'inherit',
        font: 'inherit',
    };

    return (
        <PanelHeaderWrap style={{ background: 'transparent', borderBottom: 'none' }}>
            <PanelHeaderRow>
                <PanelHeaderLeft>
                    {isGroup && onOpenGroupSidePanel ? (
                        <button
                            type="button"
                            aria-label="Open group details"
                            onClick={() => onOpenGroupSidePanel()}
                            style={{ ...titleButtonStyle, flexShrink: 0, borderRadius: '50%', cursor: 'default' }}
                        >
                            <ChatCircleAvatar
                                imageUrl={avatarImageUrl}
                                initials={initials || '?'}
                                size={32}
                                avatarBg={avatarColor?.bg}
                                avatarColor={avatarColor?.color}
                                fontSize={12}
                            />
                        </button>
                    ) : (
                        <ChatCircleAvatar
                            imageUrl={avatarImageUrl}
                            initials={initials || '?'}
                            size={32}
                            avatarBg={avatarColor?.bg}
                            avatarColor={avatarColor?.color}
                            fontSize={12}
                        />
                    )}
                    <ProjectTitleRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                        <HeaderTitleRow>
                            {isGroup && onOpenGroupSidePanel ? (
                                <button
                                    type="button"
                                    aria-label="Open group details"
                                    onClick={() => onOpenGroupSidePanel()}
                                    style={titleButtonStyle}
                                >
                                    <PanelTitle style={{ margin: 0 }}>{projectName || title || 'Chat'}</PanelTitle>
                                </button>
                            ) : (
                                <PanelTitle style={{ margin: 0 }}>{projectName || title || 'Chat'}</PanelTitle>
                            )}
                            {projectName && projectStatus != null && projectStatus !== '' && (
                                <StatusBadge status={projectStatus} size="mini" />
                            )}
                        </HeaderTitleRow>
                        {(title || subtitle) && showSubtitle && (
                            <HeaderSubtitle title={subtitle || ''}>
                                {projectName ? (
                                    <>
                                        {title ? (
                                            isGroup && onOpenGroupSidePanel ? (
                                                <button
                                                    type="button"
                                                    aria-label="Open group details"
                                                    onClick={() => onOpenGroupSidePanel()}
                                                    style={{
                                                        border: 0,
                                                        background: 'transparent',
                                                        padding: 0,
                                                        margin: 0,
                                                        cursor: 'pointer',
                                                        font: 'inherit',
                                                        fontWeight: 600,
                                                        color: 'inherit',
                                                        display: 'inline',
                                                        verticalAlign: 'baseline',
                                                        lineHeight: 'inherit',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {title}
                                                </button>
                                            ) : (
                                                <strong style={{ fontWeight: 600, color: 'inherit' }}>{title}</strong>
                                            )
                                        ) : null}
                                        {title && subtitle ? ' · ' : null}
                                        {subtitle ? <span style={{ cursor: 'default' }}>{subtitle}</span> : null}
                                    </>
                                ) : (
                                    subtitle || null
                                )}
                            </HeaderSubtitle>
                        )}

                        {isGroup && count > 0 ? (
                            <MembersInlineWrap>
                                <AnimatePresence initial={false} mode="wait">
                                    {membersExpanded ? (
                                        <motion.div
                                            key="members-expanded"
                                            variants={memberSlideContainer}
                                            initial="hidden"
                                            animate="show"
                                            exit="exit"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 0, position: 'relative' }}
                                            aria-label="Members"
                                        >
                                            {visibleMembers.map((m, index) => {
                                                const mid = m?.id ?? m?.userId ?? m?._id;
                                                const colors = getAvatarColor(mid);
                                                const name = String(m?.name || m?.email || '?');
                                                const memInitials = name
                                                    .split(/\s+/)
                                                    .map((s) => s[0])
                                                    .join('')
                                                    .toUpperCase()
                                                    .slice(0, 2);
                                                const isAdmin = adminId && String(mid) === String(adminId);
                                                const memAvatarUrl = resolveChatAvatarUrl(
                                                    m?.avatarUrl ?? getParticipantAvatarPath(m),
                                                );
                                                return (
                                                    <motion.div
                                                        key={String(mid)}
                                                        variants={memberSlideItem}
                                                        style={{
                                                            position: 'relative',
                                                            zIndex: 10 + visibleMembers.length - index,
                                                            marginLeft: index === 0 ? 0 : -expandedMemberOverlap,
                                                        }}
                                                    >
                                                        <div style={{ position: 'relative' }} title={name}>
                                                            <MemberAvatar
                                                                style={{
                                                                    background: 'transparent',
                                                                    padding: 0,
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <ChatCircleAvatar
                                                                    imageUrl={memAvatarUrl}
                                                                    initials={memInitials || '?'}
                                                                    size={34}
                                                                    avatarBg={colors.bg}
                                                                    avatarColor={colors.color}
                                                                />
                                                            </MemberAvatar>
                                                            {isAdmin ? (
                                                                <MemberAdminCrownBadge title="Admin">
                                                                    <Crown
                                                                        size={10}
                                                                        color="#78350f"
                                                                        strokeWidth={2.25}
                                                                        aria-hidden
                                                                    />
                                                                </MemberAdminCrownBadge>
                                                            ) : null}
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                            <motion.div
                                                variants={memberSlideItem}
                                                style={{ position: 'relative', zIndex: 50, marginLeft: 6 }}
                                            >
                                                <MemberIconAvatarButton
                                                    type="button"
                                                    aria-label="Close members"
                                                    title="Close"
                                                    onClick={() => setMembersExpanded(false)}
                                                >
                                                    <LeftOutlined style={{ fontSize: 12 }} />
                                                </MemberIconAvatarButton>
                                            </motion.div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="members-collapsed"
                                            initial={{ opacity: 0.96, x: -6 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -8 }}
                                            transition={{ duration: membersReducedMotion ? 0.01 : 0.22 }}
                                            style={{ display: 'inline-flex' }}
                                        >
                                            <MembersCountButton
                                                type="button"
                                                onClick={() => setMembersExpanded(true)}
                                                aria-label="View all members"
                                                title="View all members"
                                            >
                                                <span
                                                    style={{
                                                        position: 'relative',
                                                        width: stackClusterWidth,
                                                        height: 34,
                                                        flexShrink: 0,
                                                        display: stackPreview.length ? 'inline-block' : 'none',
                                                    }}
                                                    aria-hidden
                                                >
                                                    {stackPreview.map((m, i) => {
                                                        const mid = m?.id ?? m?.userId ?? m?._id;
                                                        const colors = getAvatarColor(mid);
                                                        const name = String(m?.name || m?.email || '?');
                                                        const memInitials = name
                                                            .split(/\s+/)
                                                            .map((s) => s[0])
                                                            .join('')
                                                            .toUpperCase()
                                                            .slice(0, 2);
                                                        const isAdmin = adminId && String(mid) === String(adminId);
                                                        const memAvatarUrl = resolveChatAvatarUrl(
                                                            m?.avatarUrl ?? getParticipantAvatarPath(m),
                                                        );
                                                        return (
                                                            <span
                                                                key={String(mid)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    left: i * stackOverlap,
                                                                    top: 0,
                                                                    zIndex: stackPreview.length - i,
                                                                }}
                                                            >
                                                                <div style={{ position: 'relative' }} title={name}>
                                                                    <MemberAvatarStack
                                                                        style={{
                                                                            background: 'transparent',
                                                                            padding: 0,
                                                                            overflow: 'hidden',
                                                                        }}
                                                                    >
                                                                        <ChatCircleAvatar
                                                                            imageUrl={memAvatarUrl}
                                                                            initials={memInitials || '?'}
                                                                            size={30}
                                                                            avatarBg={colors.bg}
                                                                            avatarColor={colors.color}
                                                                            fontSize={12}
                                                                        />
                                                                    </MemberAvatarStack>
                                                                    {isAdmin ? (
                                                                        <MemberAdminCrownBadge title="Admin">
                                                                            <Crown
                                                                                size={10}
                                                                                color="#78350f"
                                                                                strokeWidth={2.25}
                                                                                aria-hidden
                                                                            />
                                                                        </MemberAdminCrownBadge>
                                                                    ) : null}
                                                                </div>
                                                            </span>
                                                        );
                                                    })}
                                                </span>
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: 'nowrap' }}>
                                                        {count} Members
                                                    </span>
                                                    <RightOutlined style={{ fontSize: 11, opacity: 0.55 }} />
                                                </span>
                                            </MembersCountButton>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </MembersInlineWrap>
                        ) : null}
                    </ProjectTitleRow>
                </PanelHeaderLeft>
                <PanelHeaderRight>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {isSearching ? (
                            <>
                                <SearchInput
                                    value={searchValue}
                                    onChange={(e) => onSearchChange?.(e.target.value)}
                                    placeholder="Search messages"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') onSearchSubmit?.();
                                    }}
                                />
                                <IconButton type="button" aria-label="Close search" onClick={onSearchToggle}>
                                    <CloseOutlined />
                                </IconButton>
                            </>
                        ) : (
                            <IconButton type="button" aria-label="Search messages" onClick={onSearchToggle}>
                                <SearchOutlined />
                            </IconButton>
                        )}

                        {onOpenChatFiles && !(isGroup && canManageGroup) ? (
                            <IconButton type="button" aria-label="Files, media, and voice notes" onClick={() => onOpenChatFiles?.()}>
                                <FolderOutlined />
                            </IconButton>
                        ) : null}

                        {isGroup && canManageGroup ? (
                            <Dropdown
                                trigger={['click']}
                                placement="bottomRight"
                                menu={{
                                    items: [
                                        { key: 'rename', icon: <EditOutlined />, label: 'Rename group' },
                                        { key: 'add', icon: <UserAddOutlined />, label: 'Add member' },
                                        { key: 'remove', icon: <UserDeleteOutlined />, label: 'Remove member' },
                                        { type: 'divider', key: 'files-divider' },
                                        { key: 'files', icon: <FolderOutlined />, label: 'Files' },
                                    ],
                                    onClick: ({ key }) => {
                                        if (key === 'rename') return onRenameGroup?.() ?? antMessage.info('Rename group');
                                        if (key === 'add') return onAddMember?.() ?? antMessage.info('Add member');
                                        if (key === 'remove') return onRemoveMember?.() ?? antMessage.info('Remove member');
                                        if (key === 'files') return onOpenChatFiles?.() ?? antMessage.info('Files');
                                    },
                                }}
                            >
                                <IconButton type="button" aria-label="Conversation menu">
                                    <EllipsisOutlined style={{ transform: 'rotate(90deg)' }} />
                                </IconButton>
                            </Dropdown>
                        ) : null}

                        {selectionCount > 0 ? (
                            <>
                                <IconButton type="button" aria-label="Pin selected" onClick={onPin}>
                                    <PushpinOutlined />
                                </IconButton>
                                <IconButton type="button" aria-label="Info" onClick={onInfo}>
                                    <InfoCircleOutlined />
                                </IconButton>
                            </>
                        ) : null}
                    </div>
                </PanelHeaderRight>
            </PanelHeaderRow>
        </PanelHeaderWrap>
    );
}
